import * as Sentry from '@sentry/react';
import { SeverityLevel } from '@sentry/react';

import { APP_CONFIG } from '@/constants/appConfig';

type LogContext = Record<string, unknown>;
type ErrorWithExtras = {
    data?: LogContext;
    message?: string;
    title?: string;
};
type CaptureOptions = {
    extras?: LogContext;
    fingerprint?: string[];
};

function init() {
    if (!process.env.__DEV__) {
        Sentry.init({
            dsn: APP_CONFIG.sentryDns,
            environment: APP_CONFIG.environmentName,
            tracesSampler: (context) => {
                return context?.request?.url?.includes('sync/changes') ||
                    process.env.ENVIRONMENT_NAME !== 'production'
                    ? 1.0
                    : 0.7;
            },
        });
    }
}

function addErrorExtras(err: ErrorWithExtras, scope: Sentry.Scope) {
    const errDataKeys = err.data && Object.keys(err.data);

    if (errDataKeys && errDataKeys.length) {
        errDataKeys.forEach((key) => scope.setExtra(key, err.data![key]));
    }
}

function normalizeError(err: unknown) {
    if (typeof err === 'object' && err !== null) {
        const errorWithExtras = err as ErrorWithExtras;
        const capturedError = new Error();
        capturedError.name = errorWithExtras.title || 'Error';
        capturedError.message = errorWithExtras.message || 'Error message';

        return { capturedError, errorWithExtras };
    }

    return { capturedError: err };
}

function addCaptureOptions(options: CaptureOptions | undefined, scope: Sentry.Scope) {
    if (options?.extras) {
        Object.entries(options.extras).forEach(([key, value]) =>
            scope.setExtra(key, value),
        );
    }

    if (options?.fingerprint) {
        scope.setFingerprint(options.fingerprint);
    }
}

function shouldCaptureException(level?: SeverityLevel) {
    return !level || level === 'error' || level === 'fatal';
}

function getMessage(capturedError: unknown) {
    if (capturedError instanceof Error) {
        return `${capturedError.name}: ${capturedError.message}`;
    }

    return String(capturedError);
}

function recordMessage(
    message: string,
    level: SeverityLevel = 'info',
    options?: CaptureOptions,
) {
    Sentry.withScope((scope) => {
        scope.setLevel(level);
        addCaptureOptions(options, scope);
        Sentry.captureMessage(message);
    });
}

function recordError(
    err: unknown,
    level?: SeverityLevel,
    options?: CaptureOptions,
) {
    const { capturedError, errorWithExtras } = normalizeError(err);

    Sentry.withScope((scope) => {
        if (errorWithExtras) {
            addErrorExtras(errorWithExtras, scope);
        }

        if (level) {
            scope.setLevel(level);
        }

        addCaptureOptions(options, scope);

        if (!shouldCaptureException(level)) {
            Sentry.captureMessage(getMessage(capturedError));
            return;
        }

        Sentry.captureException(capturedError);
    });
}

function setUser(user: string) {
    Sentry.setUser({ id: user });
}

export default {
    init,
    recordError,
    recordMessage,
    setUser,
    shouldCaptureException,
};
